/*
 * A card the basic report can't fill, shown as a sample of what the advanced
 * one would say.
 *
 * The point is that the viewer UNDERSTANDS the card — the bars, the radar, the
 * court and every label stay sharp, because the shape of the analysis is the
 * pitch. Only the figures are fogged, so nothing invented can be read as a
 * measurement. That's done in CSS by default-deny (see .locked-sample): every
 * text node is fogged, and the structural chrome — titles, axis captions,
 * legend names — is explicitly restored. A value class nobody whitelisted
 * stays fogged rather than leaking a fabricated number.
 *
 * On top of that the sample is inert: `pointer-events: none` so it cannot be
 * interacted with, and `aria-hidden` so screen readers never read the made-up
 * figures. They get the header text instead.
 */
import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import { CAN_UNLOCK, REPORT_SKIN, requestUnlock } from '@/lib/utils';
import { BottomSheetShell } from '../report/BottomSheetShell';

/**
 * "Desbloquear" on what the basic report can't show.
 * - Matchlytics skin: a button that asks the dashboard for the upgrade where
 *   there's one to ask (CAN_UNLOCK); otherwise a plain label, since a pill
 *   that looks pressable but does nothing is worse than one that plainly
 *   doesn't.
 * - TiePlayer (default) skin: the advanced offer is bought when booking, so
 *   the button tells where — at the reservation's checkout.
 */
export const UnlockCta: React.FC<{
  className: string;
  children: React.ReactNode;
}> = ({ className, children }) => {
  const { t } = useTranslation('tournament');
  const [hintOpen, setHintOpen] = useState(false);

  if (REPORT_SKIN === 'matchlytics') {
    return CAN_UNLOCK ? (
      <button type="button" className={className} onClick={requestUnlock}>
        {children}
      </button>
    ) : (
      <span className={className}>{children}</span>
    );
  }

  return (
    <>
      <button
        type="button"
        className={className}
        onClick={() => setHintOpen(true)}
      >
        {children}
      </button>
      <BottomSheetShell open={hintOpen} onClose={() => setHintOpen(false)}>
        <div className="flex items-start gap-3 pr-8">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sky-50 text-sky-600">
            <Lock size={16} strokeWidth={2.4} aria-hidden="true" />
          </span>
          <p className="pt-1.5 text-base font-semibold text-slate-800">
            {t('locked.tieplayerHint')}
          </p>
        </div>
      </BottomSheetShell>
    </>
  );
};

interface LockedPreviewProps {
  /** The real card, rendered with sample data. */
  children: React.ReactNode;
  /** One line naming what this card would tell them. */
  hook: string;
}

const LockedPreview: React.FC<LockedPreviewProps> = ({ children, hook }) => {
  const { t } = useTranslation('tournament');

  return (
    <div className="locked">
      {/* Leads the block: the pitch is read before the mocked card it's
          pitching, so nobody starts by trying to read the figures. */}
      <div className="locked-head">
        <p className="locked-hook">
          <Lock size={12} strokeWidth={2.4} aria-hidden="true" />
          {hook}
        </p>
        <UnlockCta className="locked-cta">{t('locked.cta')}</UnlockCta>
      </div>

      <div className="locked-sample" aria-hidden="true">
        {children}
        {/* Pinned to the card, so the disclaimer travels with the figures it
            disclaims — a screenshot of the card carries it too. Scoped to the
            VALUES on purpose: the layout, labels and chart types are the real
            ones, and "example card" would undersell what is being pitched. */}
        <span className="locked-note">{t('locked.illustrative')}</span>
      </div>
    </div>
  );
};

export default LockedPreview;
